/**
 * ONE TAP — persisted player settings.
 *
 * One store, one localStorage key, one place that knows how to turn a stored blob back
 * into something safe to hand to the renderer, the audio engine and the input manager.
 *
 * Two rules govern this file:
 *
 *  1. **Never trust what came out of storage.** It survives version bumps, it is editable
 *     by anything running on the page, and a `NaN` sensitivity or a `null` binding table
 *     would take the whole game down at the worst possible moment. Every field is coerced
 *     and clamped on load; anything unrecognisable falls back to its default rather than
 *     throwing the entire settings object away, so one bad key cannot wipe a player's
 *     rebinds.
 *  2. **Storage is optional.** Private browsing, disabled cookies and quota exhaustion all
 *     make localStorage throw. The game must run identically without it, just forgetfully.
 *
 * The targets are described structurally rather than by importing `Renderer`,
 * `AudioEngine` and `InputManager`. That keeps the store at the bottom of the dependency
 * graph — it is imported by the bootstrap, and imports nothing but the shared contract.
 */

import { MOUSE_SENSITIVITY_DEFAULT } from '@shared/constants';
import { clamp } from '@shared/math';
import { normalizeBindings, defaultBindings, type Bindings } from '@client/input/bindings';

/**
 * Deliberately *not* `onetap.settings.v1` — the UI module owns that key for the values it
 * renders sliders for (sensitivity, volumes, FOV, colourblind). This store owns the things
 * the UI has no screen for: key bindings, gamepad tuning and the dev overlay. The bootstrap
 * seeds the overlapping fields from the UI so the engine and the sliders always agree.
 */
const STORAGE_KEY = 'onetap.client.v1';

/** Bump when a field's *meaning* changes; unknown versions are re-derived from defaults. */
const SCHEMA_VERSION = 1;

// ---------------------------------------------------------------------------
// Shape
// ---------------------------------------------------------------------------

export type QualitySetting = 'auto' | 'low' | 'medium' | 'high';

const QUALITY_VALUES: readonly QualitySetting[] = ['auto', 'low', 'medium', 'high'];

export interface Settings {
  /** Last name the player used. Pre-fills the menu so nobody types it twice. */
  playerName: string;
  /** Radians of view rotation per pixel of raw mouse motion. */
  sensitivity: number;
  invertY: boolean;
  /** Master output gain, 0..1. */
  volume: number;
  quality: QualitySetting;
  gamepadEnabled: boolean;
  /** Radians/second of turn at full right-stick deflection. */
  gamepadLookSpeed: number;
  gamepadDeadzone: number;
  /** Leave the developer overlay on across sessions. */
  showStats: boolean;
  bindings: Bindings;
}

/** Guard rails. A slider that lets a player set sensitivity to zero is a bug, not a feature. */
const LIMITS = {
  sensitivity: { min: 0.0002, max: 0.012 },
  volume: { min: 0, max: 1 },
  gamepadLookSpeed: { min: 0.6, max: 8.0 },
  gamepadDeadzone: { min: 0.02, max: 0.5 },
  nameLength: 16,
} as const;

export function defaultSettings(): Settings {
  return {
    playerName: '',
    sensitivity: MOUSE_SENSITIVITY_DEFAULT,
    invertY: false,
    // Deliberately conservative on first run. The mix is hot by design (the limiter sits at
    // -1 with a slow attack so the gunshot transient passes through untouched), and a new
    // player should never be startled on their first load. They can raise it in Settings.
    volume: 0.35,
    quality: 'auto',
    gamepadEnabled: true,
    gamepadLookSpeed: 3.4,
    gamepadDeadzone: 0.16,
    showStats: false,
    bindings: defaultBindings(),
  };
}

// ---------------------------------------------------------------------------
// Coercion
// ---------------------------------------------------------------------------

function num(v: unknown, fallback: number, min: number, max: number): number {
  const n = typeof v === 'number' ? v : typeof v === 'string' ? Number(v) : NaN;
  if (!Number.isFinite(n)) return fallback;
  return clamp(n, min, max);
}

function bool(v: unknown, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/**
 * Names are shown to other players, so they get the same treatment the server gives them:
 * strip control characters, collapse whitespace, cap the length.
 */
export function sanitizePlayerName(raw: unknown): string {
  if (typeof raw !== 'string') return '';
  const stripped = raw
    .replace(/[\u0000-\u001f\u007f-\u009f]/g, '')
    .replace(/\s+/g, ' ')
    .trim();
  return stripped.slice(0, LIMITS.nameLength);
}

export function normalizeSettings(raw: unknown): Settings {
  const out = defaultSettings();
  if (!raw || typeof raw !== 'object') return out;
  const src = raw as Record<string, unknown>;

  out.playerName = sanitizePlayerName(src.playerName);
  out.sensitivity = num(src.sensitivity, out.sensitivity, LIMITS.sensitivity.min, LIMITS.sensitivity.max);
  out.invertY = bool(src.invertY, out.invertY);
  out.volume = num(src.volume, out.volume, LIMITS.volume.min, LIMITS.volume.max);
  out.quality = QUALITY_VALUES.includes(src.quality as QualitySetting)
    ? (src.quality as QualitySetting)
    : out.quality;
  out.gamepadEnabled = bool(src.gamepadEnabled, out.gamepadEnabled);
  out.gamepadLookSpeed = num(
    src.gamepadLookSpeed,
    out.gamepadLookSpeed,
    LIMITS.gamepadLookSpeed.min,
    LIMITS.gamepadLookSpeed.max,
  );
  out.gamepadDeadzone = num(
    src.gamepadDeadzone,
    out.gamepadDeadzone,
    LIMITS.gamepadDeadzone.min,
    LIMITS.gamepadDeadzone.max,
  );
  out.showStats = bool(src.showStats, out.showStats);
  out.bindings = normalizeBindings(src.bindings);

  return out;
}

/**
 * A sensitivity value coming from a UI slider could plausibly be either "radians per
 * pixel" (what the engine wants) or a human-facing multiplier like the 0.1–10 range every
 * other shooter uses. The two ranges do not overlap, so we can accept both without
 * ambiguity instead of guessing wrong and handing the player a view that spins.
 */
export function coerceSensitivity(v: number): number {
  if (!Number.isFinite(v) || v <= 0) return MOUSE_SENSITIVITY_DEFAULT;
  const radiansPerPixel = v <= LIMITS.sensitivity.max ? v : MOUSE_SENSITIVITY_DEFAULT * v;
  return clamp(radiansPerPixel, LIMITS.sensitivity.min, LIMITS.sensitivity.max);
}

/** Same idea for volume: accept 0..1 or 0..100. */
export function coerceVolume(v: number): number {
  if (!Number.isFinite(v) || v < 0) return 0;
  return clamp(v > 1 ? v / 100 : v, 0, 1);
}

// ---------------------------------------------------------------------------
// Targets
// ---------------------------------------------------------------------------

/**
 * Structural views of the three subsystems settings can drive. Renderer, AudioEngine and
 * InputManager satisfy these without this module importing (or depending on) any of them.
 */
export interface RendererSettingsTarget {
  autoQuality: boolean;
  quality: 'low' | 'medium' | 'high';
}

export interface AudioSettingsTarget {
  setMasterVolume(v: number): void;
}

export interface InputSettingsTarget {
  sensitivity: number;
  invertY: boolean;
  gamepadEnabled: boolean;
  gamepadDeadzone: number;
  gamepadLookSpeed: number;
  setBindings(b: Readonly<Bindings>): void;
}

export interface SettingsTargets {
  renderer?: RendererSettingsTarget | null;
  audio?: AudioSettingsTarget | null;
  input?: InputSettingsTarget | null;
}

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

function readStorage(): unknown {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return null;
    const rec = parsed as Record<string, unknown>;
    // An unknown schema is not corrupt, just old. Fields still coerce individually, so
    // the player keeps whatever survived rather than losing everything.
    if (typeof rec.version === 'number' && rec.version > SCHEMA_VERSION) return null;
    return rec.data ?? rec;
  } catch {
    return null;
  }
}

function writeStorage(data: Settings): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ version: SCHEMA_VERSION, data }));
  } catch {
    // Private mode, disabled storage, or quota. The game does not care; it just forgets.
  }
}

export class SettingsStore {
  private state: Settings;
  private targets: SettingsTargets = {};
  private readonly listeners = new Set<(s: Readonly<Settings>) => void>();
  private saveTimer: ReturnType<typeof setTimeout> | null = null;
  /** Re-applying a quality tier reallocates shadow maps and render targets — only do it on change. */
  private appliedQuality: QualitySetting | null = null;
  /** Pushing bindings resets held state, so it must not happen on an unrelated patch. */
  private appliedBindings: Readonly<Bindings> | null = null;
  /** True when the persisted blob was missing or unusable — the menu can use this to onboard. */
  readonly firstRun: boolean;

  constructor() {
    const stored = readStorage();
    this.firstRun = stored === null;
    this.state = normalizeSettings(stored);
  }

  get(): Readonly<Settings> {
    return this.state;
  }

  /** Current binding table. Convenience for the rebinding screen. */
  get bindings(): Readonly<Bindings> {
    return this.state.bindings;
  }

  subscribe(fn: (s: Readonly<Settings>) => void): () => void {
    this.listeners.add(fn);
    return () => {
      this.listeners.delete(fn);
    };
  }

  /**
   * Bind the live subsystems. Applies the current settings immediately, so the caller
   * never has to remember to do it once at boot and again on change.
   */
  attach(targets: SettingsTargets): void {
    this.targets = targets;
    this.appliedQuality = null; // a fresh renderer has not seen our tier yet
    this.appliedBindings = null;
    this.apply();
  }

  detach(): void {
    this.targets = {};
  }

  /** Merge a partial update, re-validate, apply and persist. */
  patch(p: Partial<Settings>): void {
    const merged: Record<string, unknown> = { ...this.state, ...p };
    const next = normalizeSettings(merged);
    // `normalizeSettings` sanitises the name to empty when it is not a string; a patch
    // that never mentioned the name must not clear it.
    if (p.playerName === undefined) next.playerName = this.state.playerName;
    // Keep the *identity* of an untouched binding table: re-pushing bindings resets the
    // input manager's held keys, and a volume tweak must not drop the player's W.
    if (p.bindings === undefined) next.bindings = this.state.bindings;
    this.state = next;
    this.apply();
    this.scheduleSave();
    for (const fn of this.listeners) fn(this.state);
  }

  setBindings(b: Readonly<Bindings>): void {
    this.patch({ bindings: normalizeBindings(b) });
  }

  /** Back to factory. Keeps the player's name, because re-typing it is pure annoyance. */
  reset(): void {
    const name = this.state.playerName;
    this.state = defaultSettings();
    this.state.playerName = name;
    this.apply();
    this.scheduleSave();
    for (const fn of this.listeners) fn(this.state);
  }

  /** Push the current values into the renderer, audio engine and input manager. */
  apply(): void {
    const s = this.state;
    const { renderer, audio, input } = this.targets;

    if (renderer && this.appliedQuality !== s.quality) {
      this.appliedQuality = s.quality;
      if (s.quality === 'auto') {
        // Auto starts at the top and lets the renderer's own frame-time watchdog walk it
        // down; pinning a tier disables that watchdog entirely.
        renderer.autoQuality = true;
        renderer.quality = 'high';
      } else {
        renderer.quality = s.quality;
        renderer.autoQuality = false;
      }
    }

    if (audio) audio.setMasterVolume(s.volume);

    if (input) {
      input.sensitivity = s.sensitivity;
      input.invertY = s.invertY;
      input.gamepadEnabled = s.gamepadEnabled;
      input.gamepadDeadzone = s.gamepadDeadzone;
      input.gamepadLookSpeed = s.gamepadLookSpeed;
      if (this.appliedBindings !== s.bindings) {
        this.appliedBindings = s.bindings;
        input.setBindings(s.bindings);
      }
    }
  }

  /** Write through now. Called on page hide so nothing is lost to a debounce. */
  flush(): void {
    if (this.saveTimer !== null) {
      clearTimeout(this.saveTimer);
      this.saveTimer = null;
    }
    writeStorage(this.state);
  }

  dispose(): void {
    this.flush();
    this.listeners.clear();
    this.targets = {};
  }

  /**
   * Debounced so dragging a volume slider does not hit localStorage sixty times a second
   * — a synchronous write on the main thread during gameplay is a dropped frame.
   */
  private scheduleSave(): void {
    if (this.saveTimer !== null) clearTimeout(this.saveTimer);
    this.saveTimer = setTimeout(() => {
      this.saveTimer = null;
      writeStorage(this.state);
    }, 250);
  }
}
